import { Inject, Injectable, PipeTransform } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { EntityService } from '@services/entity/entity.service';
import { User } from '@models/identity/user';
import { Observable } from 'rxjs';
import { RfidCard } from '@models/rfidCard';
import { UserProfile } from '../../dto/userProfile';
import { ChangePasswordRequest } from '@dto/changePasswordRequest';
import { ChangePasswordResponse } from '@dto/changePasswordResponse';
import { DeleteCurrentUserResponse } from '@dto/deleteCurrentUserResponse';
import { DeleteCurrentUserRequest } from '@dto/deleteCurrentUserRequest';
import { UpdateUserRequest } from '@dto/updateUserRequest';
import { Operator } from '@models/operator';
import { UserDto } from '@dto/userDto';
import { JWTDto } from '@dto/jwtDto';
import { __param } from 'tslib';
import { UserTableEntry } from '@dto/userTableEntry';
import { Setting } from '@models/setting';
import { CustomerPermission } from '@models/customerPermission';
import { MasterUserOfCustomer } from '@dto/masterUserOfCustomer';
import { UserForRfidAuth } from '@dto/userForRfidAuth';
import { UserEditDto } from '@dto/userEditDto';
import { CustomerDto } from '@dto/customerDto';
import { ChargingStationDto } from '@dto/chargingStationDto';
import { PatchUserProfilePicture } from '@dto/httpPatch/userProfile';
import { SupportMessage } from '@dto/supportMesagge';
import { PricingDto } from '@dto/pricingDto';
import { SubscriptionDetailDto } from '@dto/subscriptionDetailDto';
import { ConfirmationCodeDto } from '../../dto/confirmationCodeDto';
import { UpdateUserEmailRequest } from '../../dto/updateUserEmailRequest';
import { UpdateAcceptAdRequestDto } from '../../dto/updateAcceptAdRequestDto';
import { Address } from '@models/address';
import { ExistDummyRequestDto } from '../../dto/existDummyRequestDto';

@Injectable({
  providedIn: 'root'
})
export class UsersService extends EntityService {

  public entityRoute = "users";

  constructor(http: HttpClient, @Inject('BASE_URL') baseUrl: string) {
    super(http, baseUrl);
  }

  getCurrentUserId() {
    return localStorage.getItem("userId")
  }

  async getCustomerIdOfUserAsync(userId: string): Promise<number> {
    return await this.http.get<number>(this.baseUrl + 'api/users/' + userId + '/customerId').toPromise();
  }

  async getCustomerIdOfCurrentUserAsync(): Promise<number> {
    var userId = this.getCurrentUserId();
    return await this.getCustomerIdOfUserAsync(userId);
  }

  getUsersOfCurrentCustomer(): Observable<UserDto[]> {
    return this.http.get<UserDto[]>(this.baseUrl + 'api/users');
  }

  getUsersOfCustomer(customerId: number): Observable<UserDto[]> {
    return this.http.get<UserDto[]>(this.baseUrl + 'api/customers/' + customerId + '/users');
  }

  getUserTable(): Observable<UserTableEntry[]> {
    var result = this.http.get<UserTableEntry[]>(this.baseUrl + 'api/users/table/all');
    return result;
  }

  getUserTableOfCurrentCustomer(): Observable<UserTableEntry[]> {
    return this.http.get<UserTableEntry[]>(this.baseUrl + 'api/users/table');
  }

  getUserTableOfCustomer(customerId: number): Observable<UserTableEntry[]> {
    return this.http.get<UserTableEntry[]>(this.baseUrl + 'api/customers/' + customerId + '/usersTable');
  }

  getUsersForRfidAuth(): Observable<UserForRfidAuth[]> {
    return this.http.get<UserForRfidAuth[]>(this.baseUrl + 'api/users/rfidAuth');
  }

  getUsersOfCustomerForRfidAuth(customerId: number): Observable<UserForRfidAuth[]> {
    return this.http.get<UserForRfidAuth[]>(this.baseUrl + 'api/customers/' + customerId + '/usersForRfidAuth');
  }

  getUserEditDto(userId: string): Observable<UserEditDto> {
    return this.http.get<UserEditDto>(this.baseUrl + 'api/users/' + userId + '/edit');
  }

  async getUsersOfCurrentCustomerAsync(): Promise<UserDto[]> {
    return await this.http.get<UserDto[]>(this.baseUrl + 'api/users').toPromise();
  }

  async getUsersOfCustomerAsync(customerId: number): Promise<UserDto[]> {
    return await this.http.get<UserDto[]>(this.baseUrl + 'api/customers/' + customerId + '/users').toPromise();
  }

  getLoggedInUser(): Observable<UserDto> {
    return this.http.get<UserDto>(this.baseUrl + 'api/users/current');
  }

  async getLoggedInUserAsync(): Promise<UserDto> {
    return await this.http.get<UserDto>(this.baseUrl + 'api/users/current').toPromise();
  }

  async getLoggedInUserRolesForSelectedCustomerAsync(): Promise<string[]> {
    return await this.http.get<string[]>(this.baseUrl + 'api/users/current/roles').toPromise();
  }

  getCustomersOfCurrentUser(): Observable<CustomerDto[]> {
    return this.http.get<CustomerDto[]>(this.baseUrl + 'api/users/current/customers');
  }

  getUserProfile(): Observable<UserProfile> {
    return this.http.get<UserProfile>(this.baseUrl + 'api/users/profile');
  }

  getUserAddressById(userId: string): Observable<Address> {
    return this.http.get<Address>(this.baseUrl + 'api/users/' + userId + '/address');
  }

  getUserProfileById(userId: string): Observable<UserProfile> {
    return this.http.get<UserProfile>(this.baseUrl + 'api/users/' + userId + '/profile');
  }

  getUserDtoById(userId: string): Observable<UserDto> {
    return this.http.get<UserDto>(this.baseUrl + 'api/users/' + userId + '/dto');
  }

  getUserNameById(userId: string): Observable<UserDto> {
    return this.http.get<UserDto>(this.baseUrl + 'api/users/' + userId + '/name' );
  }

  getUserProfileOfCustomer(customerId: number): Observable<UserProfile> {
    return this.http.get<UserProfile>(this.baseUrl + 'api/customers/' + customerId + '/profile');
  }

  getUserByEmail(email: string): Observable<User> {
    return this.http.get<User>(this.baseUrl + 'api/users/email/' + email);
  }

  getRfidCardsOfUser(userId: string): Observable<RfidCard[]> {
    return this.http.get<RfidCard[]>(this.baseUrl + 'api/users/' + userId + '/RfidCards');
  }

  getOperatorOfUser(userId: string): Observable<Operator> {
    return this.http.get<Operator>(this.baseUrl + 'api/users/' + userId + '/operator');
  }

  getUsersWithTransactionsAtChargingStation(chargingStationId: string): Observable<UserDto[]> {
    return this.http.get<UserDto[]>(this.baseUrl + 'api/users/withTransactionsAtChargingStation/' + chargingStationId);
  }

  getUsersWithTransactionsAtChargingStations(chargingStations: ChargingStationDto[]): Observable<UserDto[]> {
    let params = new HttpParams()
      .set('chargingStationIdsString', chargingStations.map(x => x.id).join(','));

    return this.http.get<UserDto[]>(this.baseUrl + 'api/users/withTransactionsAtChargingStations', { params: params });
  }

  async getCurrentCustomerPermissionsAsync(): Promise<CustomerPermission[]> {
    return await this.http.get<CustomerPermission[]>(this.baseUrl + 'api/Users/current/permissions').toPromise();
  }

  async getMasterUserOfCurrentCustomerAsync(): Promise<MasterUserOfCustomer> {
    return await this.http.get<MasterUserOfCustomer>(this.baseUrl + 'api/Users/current/masterUser').toPromise();
  }

  checkIfMasterUser(userId: string): Observable<boolean> {
    return this.http.get<boolean>(this.baseUrl + 'api/users/' + userId + '/isMasterUser');
  }

  createUserForCurrentCustomer(user: User): Observable<User> {
    return this.http.post<User>(this.baseUrl + 'api/users', user);
  }

  createAnonymousUserForCurrentCustomer(user: User): Observable<User> {
    return this.http.post<User>(this.baseUrl + 'api/users/anonym', user);
  }

  createUserForCustomer(customerId: number, user: User): Observable<User> {
    return this.http.post<User>(this.baseUrl + 'api/customers/' + customerId + '/user', user);
  }

  createAnonymousUserForCustomer(customerId: number, user: User): Observable<User> {
    return this.http.post<User>(this.baseUrl + 'api/customers/' + customerId + '/user/anonym', user);
  }

  updateUser(userId: string, updateUserRequest: UpdateUserRequest): Observable<boolean> {
    return this.http.patch<boolean>(this.baseUrl + 'api/' + this.entityRoute + '/' + userId, updateUserRequest);
  }

  setUserRole(userId: string, role: string, customerId?: number): Observable<void> {
    const body = { role, customerId: customerId ?? null };
    return this.http.put<void>(this.baseUrl + 'api/users/' + userId + '/role', body);
  }

  // patchPricingOfUsers(pricingDto: PricingDto): Observable<PricingDto> {
  //   return this.http.patch<PricingDto>(this.baseUrl + 'api/' + this.entityRoute + '/pricing', pricingDto);
  // }

  removePricingFromUsers(pricingDto: PricingDto): Observable<PricingDto> {
    return this.http.patch<PricingDto>(this.baseUrl + 'api/' + this.entityRoute + '/pricing/remove', pricingDto);
  }

  changePasswordOfCurrentUser(changePasswordRequest: ChangePasswordRequest): Observable<ChangePasswordResponse> {
    return this.http.post<ChangePasswordResponse>(this.baseUrl + 'api/' + this.entityRoute + '/changepassword', changePasswordRequest);
  }

  changePasswordOfCustomer(changePasswordRequest: ChangePasswordRequest, customerId: number): Observable<ChangePasswordResponse> {
    return this.http.post<ChangePasswordResponse>(this.baseUrl + 'api/customers/' + customerId + '/changepassword', changePasswordRequest);
  }

  patchUserProfile(body, userId: string) {
    return this.http.patch(this.baseUrl + 'api/users/'+ userId +'/profile', body);
  }

  patchMasterUserProfile(body, userId: string) {
    return this.http.patch(this.baseUrl + 'api/users/' + userId + '/profile', body);
  }

  patchCurrentUserProfile(body) {
    return this.http.patch(this.baseUrl + 'api/users/profile', body);
  }

  patchUserProfileOfCurrentCustomer(userProfile: UserProfile) {
    return this.http.patch(this.baseUrl + 'api/users/' + 'patchUserProfileOfCurrentCustomer/', userProfile);
  }

  patchUserProfileOfCustomer(customerId: number, userProfile: UserProfile){
    return this.http.patch(this.baseUrl + 'api/users/' + 'patchUserProfileOfCustomer/' + customerId , userProfile);
  }

  patchProfilePicture(body: PatchUserProfilePicture) {
    return this.http.patch(this.baseUrl + 'api/users/profilePicture', body);
  }

  patchProfilePictureOfUser(body: PatchUserProfilePicture, userId: string) {
    return this.http.patch(this.baseUrl + 'api/users/' + userId + '/profilePicture', body);
  }

  patchProfilePictureOfCustomer(body: PatchUserProfilePicture, customerId: number,) {
    return this.http.patch(this.baseUrl + 'api/customers/' + customerId + '/profilePicture', body);
  }

  async changeSelectedCustomerOfCurrentUserAsync(customerId: number) {
    return await this.http.patch(this.baseUrl + 'api/users/current/customer/' + customerId, null).toPromise();
  }

  sendMessageToSupport(supportMessage: SupportMessage) {
    return this.http.post(this.baseUrl + 'api/users/sendmessagetosupport', supportMessage);
  }

  updateCurrentUserSettings(settings: Setting) {
    return this.http.put(this.baseUrl + 'api/users/settings', settings);
  }

  updateMasterUserOfCustomerSettings(customerId: number, settings: Setting) {
    return this.http.put(this.baseUrl + 'api/customers/' + customerId + '/settings', settings);
  }

  syncLocalOcppWhitelistForAllUsers(customerId: number): Observable<any> {
    return this.http.put<any>(this.baseUrl + 'api/customers/' + customerId + '/syncLocalOcppWhitelist', null);
  }

  deleteCurrentUser(deleteCurrentUserRequest: DeleteCurrentUserRequest): Observable<DeleteCurrentUserResponse> {
    const currentPassword = deleteCurrentUserRequest.currentPassword;

    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: {
        currentPassword
      },
    };
    return this.http.delete<DeleteCurrentUserResponse>(this.baseUrl + 'api/' + this.entityRoute + '/current/delete', options);
  }

  deleteMasterUserOfCustomer(customerId: number): Observable<DeleteCurrentUserResponse> {
    return this.http.delete<DeleteCurrentUserResponse>(this.baseUrl + 'api/customers/' + customerId + '/masterUser');
  }

  DisinviteUser(userId: string): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'api/users/DisinviteUser/' +userId, { });
  }

  getUpvotyToken(privateKey: string, userId: string, userName: string, userEmail: string): Observable<JWTDto> {
    return this.http.get<JWTDto>(`${this.baseUrl}api/users/upvoty/${privateKey}/${userId}/${userName}/${userEmail}`);
  }

  getCustomerSubscriptionDetailsOfCurrentCustomer(): Observable<SubscriptionDetailDto> {
    return this.http.get<SubscriptionDetailDto>(this.baseUrl + 'api/users/SubscriptionDetails');
  }

  getCountOfUsers(): Observable<number> {
    return this.http.get<number>(this.baseUrl + 'api/users/count')
  }

  checkIfEmailExists(email: String): Observable<boolean> {
    return this.http.get<boolean>(this.baseUrl + 'api/users/emailExists/' + email);
  }

  getMasterUserByCustomerId(customerId: number): Observable<UserDto> {
    return this.http.get<UserDto>(this.baseUrl + 'api/users/masterUser/' + customerId);
  }

  invite(userId: string, payload: any): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'api/users/invite/' + userId, payload);
  }

  static matches(user: User, term: string, pipe?: PipeTransform) {
    return user.email?.toLowerCase().includes(term.toLowerCase())
      || user.firstName?.toLowerCase().includes(term.toLowerCase())
      || user.lastName?.toLowerCase().includes(term.toLowerCase())
      || user.rfidCardsString?.toLowerCase().includes(term.toLowerCase())
      || user.allowedChargingStationsString?.toLowerCase().includes(term.toLowerCase())
      || user.company?.toLowerCase().includes(term.toLowerCase())
  }

  sendConfirmationCode(confirmationCodeDto: ConfirmationCodeDto): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}api/users/sendConfirmationCode/`, confirmationCodeDto);
  }

  isConfirmationCodeValid(confirmationCodeDto: ConfirmationCodeDto): Observable<any>{
    return this.http.post<boolean>(`${this.baseUrl}api/users/isConfirmationCodeValid/`, confirmationCodeDto)
  }

  updateUserEmailByUserId(updateUserEmailRequest: UpdateUserEmailRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}api/users/updateUserEmailByUserId/`, updateUserEmailRequest)
  }

  changeVerifiedEmailByUserId(confirmationCodeDto: ConfirmationCodeDto): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}api/users/changeVerifiedEmailByUserId/`, confirmationCodeDto)
  }

  updateAcceptAd(updateAcceptAdRequest: UpdateAcceptAdRequestDto): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}api/users/updateAcceptAd/`, updateAcceptAdRequest)
  }

  existEmail(eMail: string): Observable<any> {
    const encodedEmail = encodeURIComponent(eMail);
    return this.http.get<any>(`${this.baseUrl}api/users/existEmail/?eMail=${encodedEmail}`);
  }

  existDummyUser(requestDto: ExistDummyRequestDto): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}api/users/existDummyUser/`,requestDto);
  }
}
